import { Base } from "ui/Scripts/source/framework/base";

export interface IEventTypeNode {
    id: string;
    parentId: string;
    companyId: string;
    code: string;
    comment: string;
    activeState: number;
    eventTypeUsageType: number;
    parent: IEventTypeNode;
    children: IEventTypeNode[];
    selected: boolean;
    disabled: boolean;

    isNew(): boolean;
    isLeafNode(): boolean;
    isLeafNode(): boolean;
    isActive(): boolean;
    isSelectableForNew(): boolean;
    getPathName(): string;
    enableByIds(parentDisabled: boolean, enabledIds: string[]);
    removeChild(nodeId: string);
    removeInActiveBranches(parentActive: boolean);
    removeUnselectableBranches();
    getFirstActiveNode(): IEventTypeNode;
    getParentIdPath(): string[];
    getChildIdPath(): string[];
    getParentIdPathToId(id: string): string[];
    getEventTypeNodeById(id: string): IEventTypeNode;
    getEventTypeNodesByIds(ids: string[]): IEventTypeNode[];
}

export class EventTypeNode implements IEventTypeNode {
    id: string;
    parentId: string;
    companyId: string;
    code: string;
    comment: string;
    activeState: number;
    eventTypeUsageType: number;
    parent: EventTypeNode;
    children: EventTypeNode[];
    selected: boolean;
    disabled: boolean;

    constructor();
    constructor(obj: IEventTypeNode);
    constructor(obj?: any) {
        this.selected = false;
        this.id = obj && obj.id || "";
        this.parentId = obj && obj.parentId || "";
        this.companyId = obj && obj.companyId || "";
        this.code = obj && obj.code || "";
        this.comment = obj && obj.comment || "";
        this.activeState = obj && obj.activeState || 0;
        this.eventTypeUsageType = obj && obj.eventTypeUsageType || 0;
        this.disabled = obj && obj.disabled || false;
        this.parent = null;
        this.children = Base.getTypedArray<EventTypeNode>(obj ? obj.children : null, EventTypeNode);
        for (let i = 0; i < this.children.length; i++) {
            this.children[i].parent = this;
        }
    }

    isNew(): boolean {
        return !this.id || this.id === Base.emptyGuid;
    }

    isLeafNode(): boolean {
        return !this.children || this.children.length < 1;
    }

    isActive(): boolean {
        return this.activeState > 0.5;
    }

    isSelectableForNew(): boolean {
        return this.eventTypeUsageType > 0.5;
    }

    getPathName(): string {
        if (!this.parentId) return this.code;
        return this.parent.getPathName() + " \\ " + this.code;
    }

    removeChild(id: string) {
        const index = this.children.findIndex(i => i.id === id);
        if (index < 0) return;
        this.children.splice(index, 1);
    }

    removeInActiveBranches(parentActive: boolean) {
        const isActive = parentActive && this.isActive();
        for (let i = this.children.length - 1; i >= 0; i --) {
            this.children[i].removeInActiveBranches(isActive);
        }
        if (!this.parentId) return;
        if (this.children.length < 1 && !isActive) {
            this.parent.removeChild(this.id);
        }
    }

    getFirstActiveNode(): IEventTypeNode {
        if (this.activeState > 0.5) return this;
        for (let i = 0; i < this.children.length; i++) {
            const childResult = this.children[i].getFirstActiveNode();
            if (childResult != null) return childResult;
        }
        return null;
    }

    removeUnselectableBranches() {
        for (let i = this.children.length - 1; i >= 0; i --) {
            this.children[i].removeUnselectableBranches();
        }
        if (!this.parentId) return;
        if (!this.isSelectableForNew() || !this.isActive()) {
            this.parent.removeChild(this.id);
        }
    }

    static getSelectableEventTypeNodes(nodes: IEventTypeNode[]): IEventTypeNode[] {
        const result: IEventTypeNode[] = [];
        for (let i = 0; i < nodes.length; i++) {
            const clone = new EventTypeNode(nodes[i]);
            clone.removeUnselectableBranches();
            if (!clone.isSelectableForNew()) continue;
            result.push(clone);
        }
        return result;
    }

    initializeActiveState() {
        if (this.disabled) {
            this.activeState = 0;
            this.disabled = false;
        }
        for (let i = 0; i < this.children.length; i++) {
            this.children[i].initializeActiveState();
        }
    }

    enableByIds(parentDisabled: boolean, enabledIds: string[]) {
        if (!enabledIds || enabledIds.length < 1) return;
        const disabled = !this.isActive() && enabledIds.indexOf(this.id) > -1;
        this.disabled = parentDisabled || disabled;
        if (disabled) {
            this.activeState = 1;
            this.disabled = true;
        }
        for (let i = 0; i < this.children.length; i++) {
            this.children[i].enableByIds(this.disabled, enabledIds);
        }
    }

    static getActiveEventTypeNodes(nodes: IEventTypeNode[], selectedIds: string[]): IEventTypeNode[] {
        const result: IEventTypeNode[] = [];
        for (let i = 0; i < nodes.length; i++) {
            const clone = new EventTypeNode(nodes[i]);
            clone.initializeActiveState();
            clone.enableByIds(false, selectedIds);
            clone.removeInActiveBranches(true);
            if (!clone.getFirstActiveNode()) continue;
            result.push(clone);
        }
        return result;
    }

    getEventTypeNodeById(id: string): IEventTypeNode {
        if (!id) return null;
        if (this.id.equalIgnoreCase(id)) return this;
        for (let i = 0; i < this.children.length; i++) {
            const childResult = this.children[i].getEventTypeNodeById(id);
            if (childResult) return childResult;
        }
        return null;
    }

    static getEventTypeNodeFromNodesById(nodes: IEventTypeNode[], id: string): IEventTypeNode {
        if (!id) return null;
        for (let i = 0; i < nodes.length; i++) {
            const nodeResult = nodes[i].getEventTypeNodeById(id);
            if (nodeResult) return nodeResult;
        }
        return null;
    }

    getEventTypeNodesByIds(ids: string[]): IEventTypeNode[] {
        if (!ids || ids.length < 1) return [];
        let result: IEventTypeNode[] = [];
        if (ids.indexOf(this.id) > -1) {
            result.push(this);
        }
        for (let i = 0; i < this.children.length; i++) {
            result = result.concat(this.children[i].getEventTypeNodesByIds(ids));
        }
        return result;
    }

    static getEventTypeNodesFromNodesByIds(nodes: IEventTypeNode[], ids: string[]): IEventTypeNode[] {
        if (!nodes || !ids || nodes.length < 1 || ids.length < 1) return [];
        let result: IEventTypeNode[] = [];
        for (let i = 0; i < nodes.length; i++) {
            result = result.concat(nodes[i].getEventTypeNodesByIds(ids));
        }
        return result;
    }
    
    getParentIdPath(): string[] {
        return !this.parent
            ? [this.id]
            : [this.id].concat(this.parent.getParentIdPath());
    }
    
    getChildIdPath(): string[] {
        let result = [this.id];
        for (let i = 0; i < this.children.length; i++) {
            result = result.concat(this.children[i].getChildIdPath());
        }
        return result;
    }

    getParentIdPathToId(id: string): string[] {
        if (!id) return null;
        if (this.id === id) return [];
        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            const childResult = child.getParentIdPathToId(id);
            if (childResult) {
                childResult.push(this.id);
                return childResult;
            }
        }
        return null;
    }

    static getPathNamesString(nodes: IEventTypeNode[], ids: string[], defaultValue: string = ""): string {
        const idNodes = EventTypeNode.getEventTypeNodesFromNodesByIds(nodes, ids);
        if (idNodes.length < 1) return defaultValue;
        return idNodes.map(i => i.getPathName()).join(", ");
    }
}